/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * PROFESSIONALS MODULE - DASHBOARD ADMIN
 * ============================================================================
 * Arquivo: src/components/Dashboard_ADM/GerenciamentoProfissionaisADM.tsx
 * UPDATED: 2026-04-16 — v4.0
 * ✅ Botão de editar ao lado do "olhinho" na tabela
 * ✅ Modal de edição completo (espelha o modal de cadastro)
 * ✅ Visual dos modais melhorado
 * ✅ UpdateProfessionalDTO compatível (campos: fullName, cpf, phone, email,
 *    birthdate, address, city, state, postalCode, photoUrl, isPrimaryContact,
 *    notes) — funcao/especializacoes/experienciaAnos via metadata se necessário
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Plus, Download, Eye, Trash2, Edit,
  Loader2, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  ChevronLeft, ChevronRight, X, Upload, Camera, FileText,
  User, MapPin, Phone, Mail, Calendar, Hash, Building2,
  Award, Briefcase, Clock, AlertCircle, Save,
} from 'lucide-react';
import apiClient from '../../services/api';
import { API_BASE } from '../../utils/apiConfig';
import { glass, text } from './ui/theme';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Profissional {
  id: string;
  systemId: string;
  registroFepcssp: string;
  identidadeInternacional: string;
  fullName: string;
  cpf: string;
  email: string;
  phone: string;
  birthdate: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  photoUrl: string;
  funcao: string;
  dataInicioAtividade: string;
  experienciaAnos: number | null;
  especializacoes: string;
  affiliateId: string;
  affiliateName: string;
  createdAt: string;
}

interface Escola {
  id: string;
  schoolName: string;
  systemId: string;
  city?: string;
  state?: string;
}

interface DocSlot {
  arquivo: File | null; preview: string | null;
  enviando: boolean; enviado: boolean; erro: string | null;
}

// ─── Mapper ──────────────────────────────────────────────────────────────────
const mapProfissional = (p: any): Profissional => ({
  id:                  p.id,
  systemId:            p.systemId          ?? p.system_id           ?? '',
  registroFepcssp:     p.registroFepcssp   ?? p.registro_fepcssp    ?? '',
  identidadeInternacional: p.identidadeInternacional ?? p.identidade_internacional ?? '',
  fullName:            p.fullName          ?? p.full_name           ?? '',
  cpf:                 p.cpf               ?? '',
  email:               p.email             ?? '',
  phone:               p.phone             ?? '',
  birthdate:           p.birthdate         ?? '',
  address:             p.address           ?? '',
  city:                p.city              ?? '',
  state:               p.state             ?? '',
  postalCode:          p.postalCode        ?? p.postal_code         ?? '',
  photoUrl:            p.photoUrl          ?? p.photo_url           ?? '',
  funcao:              p.funcao            ?? '',
  dataInicioAtividade: p.dataInicioAtividade ?? p.data_inicio_atividade ?? '',
  experienciaAnos:     p.experienciaAnos   ?? p.experiencia_anos    ?? null,
  especializacoes:     p.especializacoes   ?? '',
  affiliateId:         p.affiliateId       ?? p.affiliate_id        ?? '',
  affiliateName:       p.affiliate?.schoolName ?? '',
  createdAt:           p.createdAt         ?? '',
});

const slotVazio = (): DocSlot => ({
  arquivo: null, preview: null, enviando: false, enviado: false, erro: null,
});

const fmtDate = (d: string) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
};

const inputCls = (err = false) =>
  `w-full px-4 py-3 rounded-xl border ${err ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'} ` +
  `focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white focus:border-purple-300 text-gray-900 text-sm font-medium transition-all`;

const labelCls = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS',
             'MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

// ─── Avatar ──────────────────────────────────────────────────────────────────
const ProfAvatar: React.FC<{ prof: Profissional; size?: 'sm' | 'lg' }> = ({ prof, size = 'sm' }) => {
  const [err, setErr] = useState(false);
  const sz = size === 'sm' ? 'w-10 h-10 text-base' : 'w-20 h-20 text-3xl';
  return prof.photoUrl && !err ? (
    <img src={prof.photoUrl} alt={prof.fullName}
      className={`${sz} rounded-full object-cover border-2 border-purple-200 flex-shrink-0`}
      onError={() => setErr(true)} />
  ) : (
    <div className={`${sz} rounded-full bg-gradient-to-br from-purple-500 to-indigo-600
      flex items-center justify-center text-white font-bold flex-shrink-0`}>
      {prof.fullName.charAt(0).toUpperCase()}
    </div>
  );
};

// ─── DocCard ─────────────────────────────────────────────────────────────────
const DocCard: React.FC<{
  titulo: string; descricao: string; slot: DocSlot; accept?: string;
  onSelecionar: (f: File) => void; onRemover: () => void;
}> = ({ titulo, descricao, slot, accept = 'image/*,.pdf', onSelecionar, onRemover }) => (
  <div className={`rounded-xl border-2 overflow-hidden transition-all ${
    slot.enviado   ? 'border-green-400 bg-green-50'
    : slot.arquivo ? 'border-purple-300 bg-purple-50/30'
    : 'border-dashed border-gray-200 bg-gray-50 hover:border-purple-400 hover:bg-purple-50/20'}`}>
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 bg-white/70">
      <FileText className="w-4 h-4 text-gray-400" />
      <div className="flex-1">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide leading-none">{titulo}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{descricao}</p>
      </div>
    </div>
    {slot.enviado ? (
      <div className="flex items-center gap-3 px-4 py-3">
        {slot.preview
          ? <img src={slot.preview} alt="" className="w-12 h-12 rounded-lg object-cover border border-green-300" />
          : <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center"><FileText className="w-5 h-5 text-green-600" /></div>}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-green-700 truncate">{slot.arquivo?.name}</p>
          <p className="text-[10px] text-green-500 mt-0.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Enviado</p>
        </div>
      </div>
    ) : slot.arquivo ? (
      <div className="px-4 py-3">
        {slot.preview && <img src={slot.preview} alt="" className="w-full max-h-28 object-contain rounded-lg mb-2 border border-gray-200" />}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-700 truncate flex-1">{slot.arquivo.name}</span>
          <span className="text-[10px] text-gray-400">{(slot.arquivo.size / 1024).toFixed(0)} KB</span>
          <button type="button" onClick={onRemover}
            className="p-1 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {slot.erro && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{slot.erro}</p>}
      </div>
    ) : (
      <label className="flex flex-col items-center justify-center py-5 cursor-pointer">
        <div className="w-9 h-9 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-2 shadow-sm">
          <Upload className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-xs text-gray-500 font-medium">Clique para selecionar</p>
        <p className="text-[10px] text-gray-400 mt-0.5">JPG, PNG, PDF — máx. 5 MB</p>
        <input type="file" accept={accept} className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onSelecionar(f); }} />
      </label>
    )}
  </div>
);

// ─── InfoRow ─────────────────────────────────────────────────────────────────
const InfoRow: React.FC<{ label: string; value?: string | null; icon?: React.ElementType; mono?: boolean }> = ({
  label, value, icon: Icon, mono,
}) => {
  if (!value) return null;
  return (
    <div className="bg-cosmos-50/60 rounded-xl p-3.5 border border-cosmos-100/70">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-gold-500" />}
        <p className="text-[10px] font-semibold text-cosmos-400 uppercase tracking-wider">{label}</p>
      </div>
      <p className={`font-medium text-cosmos-900 text-sm ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
};
// ─── EscolaSearch ─────────────────────────────────────────────────────────────
const EscolaSearch: React.FC<{
  escolas: Escola[];
  loading?: boolean;
  value: string;
  onChange: (id: string) => void;
}> = ({ escolas, loading, value, onChange }) => {
  const [query, setQuery]       = useState('');
  const [aberto, setAberto]     = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  const escolaSelecionada = escolas.find(e => e.id === value);

 const filtradas = query.trim().length === 0
  ? escolas.slice(0, 100)
  : escolas.filter(e =>
      e.schoolName.toLowerCase().includes(query.toLowerCase()) ||
      (e.city ?? '').toLowerCase().includes(query.toLowerCase()) ||
      (e.systemId ?? '').toLowerCase().includes(query.toLowerCase())
    ).slice(0, 100);

  useEffect(() => {
    const handler = (ev: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node))
        setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selecionar = (id: string) => {
    onChange(id);
    setQuery('');
    setAberto(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!aberto) { if (e.key === 'ArrowDown' || e.key === 'Enter') setAberto(true); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtradas.length - 1)); }
    if (e.key === 'ArrowUp')    { e.preventDefault(); setHighlight(h => Math.max(h - 1, -1)); }
    if (e.key === 'Enter')      { e.preventDefault(); if (highlight >= 0) selecionar(filtradas[highlight].id); }
    if (e.key === 'Escape')     { setAberto(false); setHighlight(-1); }
  };

  return (
    <div ref={wrapRef} className="relative">
      {/* Input de busca */}
      <div
        className={`flex items-center gap-2 w-full px-4 py-3 rounded-xl border
          ${aberto ? 'border-purple-400 ring-2 ring-purple-500 bg-white' : 'border-gray-200 bg-gray-50'}
          focus-within:outline-none transition-all cursor-text`}
        onClick={() => setAberto(true)}
      >
        <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          value={aberto ? query : (escolaSelecionada ? escolaSelecionada.schoolName : '')}
          onChange={e => { setQuery(e.target.value); setAberto(true); setHighlight(-1); }}
          onFocus={() => setAberto(true)}
          onKeyDown={handleKeyDown}
          placeholder={escolaSelecionada ? escolaSelecionada.schoolName : 'Buscar escola...'}
          className="flex-1 bg-transparent outline-none text-sm font-medium text-gray-900 placeholder-gray-400 min-w-0"
        />
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 flex-shrink-0" />}
        {escolaSelecionada && !aberto && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); selecionar(''); }}
            className="p-0.5 rounded text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Escola selecionada (tag) */}
      {escolaSelecionada && !aberto && (
        <p className="text-[11px] text-purple-600 font-semibold mt-1 ml-1">
          {escolaSelecionada.city && `${escolaSelecionada.city}${escolaSelecionada.state ? `/${escolaSelecionada.state}` : ''}`}
        </p>
      )}

      {/* Dropdown */}
      {aberto && (
        <div className="absolute z-50 mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          {!query.trim() && (
            <p className="px-4 py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
              Cadastros recentes
            </p>
          )}
          {filtradas.length === 0 ? (
            <div className="px-4 py-5 text-center text-sm text-gray-400 font-medium">
              Nenhuma escola encontrada
            </div>
          ) : (
            <ul className="max-h-56 overflow-y-auto py-1">
              {/* opção "Nenhuma" */}
              <li
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors text-sm
                  ${highlight === -1 && value === '' ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                onMouseDown={() => selecionar('')}
                onMouseEnter={() => setHighlight(-1)}
              >
                <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </div>
                <span className="text-gray-500 font-medium">Nenhuma escola vinculada</span>
              </li>

              {filtradas.map((escola, i) => (
                <li
                  key={escola.id}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors
                    ${highlight === i ? 'bg-purple-50'
                      : value === escola.id ? 'bg-indigo-50'
                      : 'hover:bg-gray-50'}`}
                  onMouseDown={() => selecionar(escola.id)}
                  onMouseEnter={() => setHighlight(i)}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0
                    ${value === escola.id
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-500'}`}>
                    {escola.schoolName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${value === escola.id ? 'text-purple-700' : 'text-gray-900'}`}>
                      {escola.schoolName}
                    </p>
                    {(escola.city || escola.state) && (
                      <p className="text-[11px] text-gray-400 truncate">
                        {[escola.city, escola.state].filter(Boolean).join(' / ')}
                      </p>
                    )}
                  </div>
                  {value === escola.id && <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
// ─── Componente principal ─────────────────────────────────────────────────────
const GerenciamentoProfissionaisADM: React.FC = () => {
  const [profissionais, setProfissionais] = useState<Profissional[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginaAtual, setPaginaAtual]     = useState(1);
  const [buscaInput, setBuscaInput]       = useState('');
  const [busca, setBusca]                 = useState('');
  const [salvando, setSalvando]           = useState(false);

   const [escolas, setEscolas] = useState<Escola[]>([]);
   const [escolasRefresh, setEscolasRefresh] = useState(0);
  const [escolasLoading, setEscolasLoading] = useState(false);

  const [modalDetalhe, setModalDetalhe]   = useState<Profissional | null>(null);
  const [modalCadastro, setModalCadastro] = useState(false);
  const [modalEditar, setModalEditar]     = useState<Profissional | null>(null);
  const [etapa, setEtapa]                 = useState<1 | 2 | 3>(1);
  const [erro, setErro]                   = useState<string | null>(null);
  const [erroEdit, setErroEdit]           = useState<string | null>(null);
  const [profId, setProfId]               = useState<string | null>(null);
  const [sucesso, setSucesso]             = useState<string | null>(null);

  // Etapa 1 — Cadastro
  const [form, setForm] = useState({
    fullName: '', cpf: '', birthdate: '', phone: '', email: '',
    address: '', city: '', state: '', postalCode: '',
    affiliateId: '', registroFepcssp: '', identidadeInternacional: '',
  });
  const [formErros, setFormErros] = useState<Record<string, string>>({});
  const [systemId, setSystemId]   = useState('');
  const [gerandoId, setGerandoId] = useState(false);
  const [idDisponivel, setIdDisponivel] = useState<boolean | null>(null);
  const [cepLoading, setCepLoading]     = useState(false);

  // Etapa 2 — Cadastro
  const [form2, setForm2] = useState({
    funcao: '', dataInicioAtividade: '',
    experienciaAnos: '', especializacoes: '',
  });

  // Etapa 3 — Cadastro
  const [registroGerado, setRegistroGerado] = useState('');
  const [fotoFile, setFotoFile]             = useState<File | null>(null);
  const [fotoPreview, setFotoPreview]       = useState<string | null>(null);
  const [slotDoc, setSlotDoc]               = useState<DocSlot>(slotVazio());
  const fotoRef = useRef<HTMLInputElement>(null);

  // Form edição
  const [formEdit, setFormEdit] = useState<{
    fullName: string; cpf: string; birthdate: string; phone: string; email: string;
    address: string; city: string; state: string; postalCode: string;
    affiliateId: string; registroFepcssp: string; identidadeInternacional: string;
    funcao: string; dataInicioAtividade: string; experienciaAnos: string; especializacoes: string;
  }>({
    fullName: '', cpf: '', birthdate: '', phone: '', email: '',
    address: '', city: '', state: '', postalCode: '',
    affiliateId: '', registroFepcssp: '', identidadeInternacional: '',
    funcao: '', dataInicioAtividade: '', experienciaAnos: '', especializacoes: '',
  });
  const [cepLoadingEdit, setCepLoadingEdit] = useState(false);
  const [fotoFileEdit, setFotoFileEdit]     = useState<File | null>(null);
  const [fotoPreviewEdit, setFotoPreviewEdit] = useState<string | null>(null);
  const fotoRefEdit = useRef<HTMLInputElement>(null);

  const itensPorPagina = 20;

  // ─── Fetch escolas ────────────────────────────────────────────────────────
  const fetchEscolas = useCallback(async () => {
    setEscolasLoading(true);
    try {
    const data: any = await apiClient.get('/affiliates?limit=5000&orderBy=schoolName&order=asc');
      const lista = (data.affiliates ?? data.results ?? data ?? []) as any[];
     setEscolas(lista.map(a => ({
  id:         a.id,
  schoolName: a.schoolName ?? a.school_name ?? '',
  systemId:   a.systemId  ?? a.system_id   ?? '',
  city:       a.city      ?? '',
  state:      a.state     ?? '',
})).sort((a, b) => a.schoolName.localeCompare(b.schoolName)));
    } catch { /* silencioso */ }
    finally { setEscolasLoading(false); }
  }, []);

  // ─── Fetch profissionais ──────────────────────────────────────────────────
  const fetchProfissionais = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let data: any;
      if (busca.trim().length >= 2) {
        data = await apiClient.get(
          `/professionals/search?query=${encodeURIComponent(busca)}&page=${paginaAtual}&limit=${itensPorPagina}`
        );
      } else {
        data = await apiClient.get(
          `/professionals?page=${paginaAtual}&limit=${itensPorPagina}`
        );
      }
      setProfissionais((data.professionals ?? data.results ?? []).map(mapProfissional));
      setTotalRegistros(data.total ?? 0);
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar profissionais');
    } finally { setLoading(false); }
  }, [busca, paginaAtual]);

  useEffect(() => {
    const t = setTimeout(() => { setBusca(buscaInput); setPaginaAtual(1); }, 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  useEffect(() => { fetchProfissionais(); }, [fetchProfissionais]);
 useEffect(() => { fetchEscolas(); }, [fetchEscolas, escolasRefresh]);

  const totalPaginas = Math.ceil(totalRegistros / itensPorPagina);

  // ─── Gerar ID PRF ─────────────────────────────────────────────────────────
  const gerarSystemId = async () => {
    setGerandoId(true); setIdDisponivel(null);
    try {
      const data: any = await apiClient.get('/professionals/generate-id').catch(() => null);
      if (data?.systemId) {
        setSystemId(data.systemId); setIdDisponivel(true);
      } else {
        const year = new Date().getFullYear();
        const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
        setSystemId(`PRF-${year}-${rand}`); setIdDisponivel(true);
      }
    } catch {
      const year = new Date().getFullYear();
      const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
      setSystemId(`PRF-${year}-${rand}`); setIdDisponivel(null);
    } finally { setGerandoId(false); }
  };

  // ─── CEP ─────────────────────────────────────────────────────────────────
  const buscarCEP = async (cep: string, modo: 'cadastro' | 'edit' = 'cadastro') => {
    const num = cep.replace(/\D/g, '');
    if (num.length !== 8) return;
    if (modo === 'cadastro') setCepLoading(true);
    else setCepLoadingEdit(true);
    try {
      const res  = await fetch(`https://viacep.com.br/ws/${num}/json/`);
      const data = await res.json();
      if (!data.erro) {
        if (modo === 'cadastro') {
          setForm(p => ({ ...p, address: data.logradouro || p.address, city: data.localidade || p.city, state: data.uf || p.state }));
        } else {
          setFormEdit(p => ({ ...p, address: data.logradouro || p.address, city: data.localidade || p.city, state: data.uf || p.state }));
        }
      }
    } finally {
      if (modo === 'cadastro') setCepLoading(false);
      else setCepLoadingEdit(false);
    }
  };

  // ─── Validar etapa 1 ──────────────────────────────────────────────────────
  const validar1 = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim())  e.fullName  = 'Obrigatório';
    if (form.cpf.replace(/\D/g, '').length !== 11) e.cpf = 'CPF inválido';
    if (!form.birthdate)        e.birthdate = 'Obrigatório';
    if (!form.phone.trim())     e.phone     = 'Obrigatório';
    if (!systemId.trim())       e.systemId  = 'Gere ou informe um ID';
    if (form.identidadeInternacional && !/^[A-Z0-9]{15}$/.test(form.identidadeInternacional)) {
      e.identidadeInternacional = 'Informe exatamente 15 letras ou números';
    }
    setFormErros(e);
    return Object.keys(e).length === 0;
  };

  // ─── Cadastrar ────────────────────────────────────────────────────────────
  const handleCadastrar = async () => {
    setSalvando(true); setErro(null);
    try {
      const payload: Record<string, any> = {
        fullName:            form.fullName.trim(),
        cpf:                 form.cpf.replace(/\D/g, ''),
        phone:               form.phone.trim()    || undefined,
        email:               form.email.trim()    || undefined,
        birthdate:           form.birthdate       || undefined,
        address:             form.address.trim()  || undefined,
        city:                form.city.trim()     || undefined,
        state:               form.state           || undefined,
        postalCode:          form.postalCode.replace(/\D/g, '') || undefined,
        systemId:            systemId.trim()      || undefined,
        registroFepcssp:     form.registroFepcssp.trim() || undefined,
        identidadeInternacional: form.identidadeInternacional || undefined,
        ...(form.affiliateId.trim() ? { affiliateId: form.affiliateId.trim() } : {}),
        funcao:              form2.funcao.trim()  || undefined,
        dataInicioAtividade: form2.dataInicioAtividade || undefined,
        experienciaAnos:     form2.experienciaAnos ? Number(form2.experienciaAnos) : undefined,
        especializacoes:     form2.especializacoes.trim() || undefined,
      };

      const data: any = await apiClient.post('/professionals', payload);
      setProfId(data.id ?? data.professional?.id);
      const prof: any = await apiClient.get(`/professionals/${data.id ?? data.professional?.id}`);
      setRegistroGerado(prof.registroFepcssp ?? '');
      setEtapa(3);
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setErro(Array.isArray(msg) ? msg.join(', ') : (msg ?? e.message ?? 'Erro ao cadastrar'));
    } finally { setSalvando(false); }
  };

  // ─── Salvar edição ───────────────────────────────────────────────────────
  const handleSalvarEdicao = async () => {
    if (!modalEditar) return;
    if (formEdit.identidadeInternacional && !/^[A-Z0-9]{15}$/.test(formEdit.identidadeInternacional)) {
      setErroEdit('Identidade internacional deve conter exatamente 15 letras ou números');
      return;
    }
    setSalvando(true); setErroEdit(null);
    try {
      const payload: Record<string, any> = {
        fullName:        formEdit.fullName.trim()                    || undefined,
        cpf:             formEdit.cpf.replace(/\D/g, '')            || undefined,
        phone:           formEdit.phone.trim()                       || undefined,
        email:           formEdit.email.trim()                       || undefined,
        birthdate:       formEdit.birthdate                          || undefined,
        address:         formEdit.address.trim()                     || undefined,
        city:            formEdit.city.trim()                        || undefined,
        state:           formEdit.state                              || undefined,
        postalCode:      formEdit.postalCode.replace(/\D/g, '')     || undefined,
        identidadeInternacional: formEdit.identidadeInternacional.trim().toUpperCase() || undefined,
        // funcao/especializacoes/experienciaAnos ficam em metadata
        // pois UpdateProfessionalDTO base não os declara —
        // se seu DTO tiver esses campos basta descomentar:
        // funcao:              formEdit.funcao.trim()          || undefined,
        // dataInicioAtividade: formEdit.dataInicioAtividade    || undefined,
        // experienciaAnos:     formEdit.experienciaAnos ? Number(formEdit.experienciaAnos) : undefined,
        // especializacoes:     formEdit.especializacoes.trim() || undefined,
        metadata: {
          funcao:              formEdit.funcao.trim()              || null,
          dataInicioAtividade: formEdit.dataInicioAtividade        || null,
          experienciaAnos:     formEdit.experienciaAnos ? Number(formEdit.experienciaAnos) : null,
          especializacoes:     formEdit.especializacoes.trim()     || null,
          affiliateId:         formEdit.affiliateId.trim()         || null,
          registroFepcssp:     formEdit.registroFepcssp.trim()     || null,
        },
      };

      // Upload de foto se selecionada
      if (fotoFileEdit) {
        const photoUrl = await uploadArquivo(fotoFileEdit, modalEditar.id, 'foto', 'Foto do profissional');
        if (photoUrl) payload.photoUrl = photoUrl;
      }

      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
      await apiClient.patch(`/professionals/${modalEditar.id}`, payload);
      setModalEditar(null);
      await fetchProfissionais();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setErroEdit(Array.isArray(msg) ? msg.join(' | ') : (msg ?? e.message ?? 'Erro ao salvar'));
    } finally { setSalvando(false); }
  };

  // ─── Abrir modal editar ───────────────────────────────────────────────────
  const abrirEditar = (p: Profissional) => {
    setFormEdit({
      fullName:            p.fullName            ?? '',
      cpf:                 p.cpf?.replace(/\D/g,'') ?? '',
      birthdate:           p.birthdate ? p.birthdate.slice(0, 10) : '',
      phone:               p.phone               ?? '',
      email:               p.email               ?? '',
      address:             p.address             ?? '',
      city:                p.city                ?? '',
      state:               p.state               ?? '',
      postalCode:          p.postalCode?.replace(/\D/g,'') ?? '',
      affiliateId:         p.affiliateId         ?? '',
      registroFepcssp:     p.registroFepcssp     ?? '',
      identidadeInternacional: p.identidadeInternacional ?? '',
      funcao:              p.funcao              ?? '',
      dataInicioAtividade: p.dataInicioAtividade ? p.dataInicioAtividade.slice(0, 10) : '',
      experienciaAnos:     p.experienciaAnos != null ? String(p.experienciaAnos) : '',
      especializacoes:     p.especializacoes     ?? '',
    });
    setErroEdit(null);
    setFotoFileEdit(null);
    setFotoPreviewEdit(p.photoUrl ?? null);
  setModalEditar(p);
  setModalDetalhe(null);
  setEscolasRefresh(r => r + 1);
  };

  // ─── Upload foto ──────────────────────────────────────────────────────────
  const handleFoto = (e: React.ChangeEvent<HTMLInputElement>, modo: 'cadastro' | 'edit' = 'cadastro') => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setErro('Máximo 5MB'); return; }
    const r = new FileReader();
    r.onload = ev => {
      if (modo === 'cadastro') {
        setFotoFile(f);
        setFotoPreview(ev.target?.result as string);
      } else {
        setFotoFileEdit(f);
        setFotoPreviewEdit(ev.target?.result as string);
      }
    };
    r.readAsDataURL(f);
    setErro(null);
  };

  const selecionarDoc = (file: File) => {
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setSlotDoc({ arquivo: file, preview, enviando: false, enviado: false, erro: null });
  };

  const removerDoc = () => {
    if (slotDoc.preview) URL.revokeObjectURL(slotDoc.preview);
    setSlotDoc(slotVazio());
  };

  const uploadArquivo = async (
    file: File, profissionalId: string, docType: string, description: string,
  ): Promise<string | null> => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('auth_token') ?? localStorage.getItem('token') ?? '';
    const url = `${API_BASE}/documents/public-upload` +
      `?affiliateId=${encodeURIComponent(profissionalId)}` +
      `&documentType=${docType}&description=${encodeURIComponent(description)}`;
    const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (!res.ok) throw new Error(`Erro ${res.status}`);
    const d = await res.json();
    return d.url ?? d.fileUrl ?? d.path ?? null;
  };

  const handleFinalizar = async () => {
    if (!profId) return;
    setSalvando(true);
    try {
      if (fotoFile) {
        const photoUrl = await uploadArquivo(fotoFile, profId, 'foto', 'Foto do profissional');
        if (photoUrl) await apiClient.patch(`/professionals/${profId}`, { photoUrl }).catch(() => {});
      }
      if (slotDoc.arquivo && !slotDoc.enviado) {
        setSlotDoc(p => ({ ...p, enviando: true }));
        try {
          await uploadArquivo(slotDoc.arquivo, profId, 'rg', 'Documento do profissional');
          setSlotDoc(p => ({ ...p, enviando: false, enviado: true }));
        } catch (e: any) {
          setSlotDoc(p => ({ ...p, enviando: false, erro: e.message }));
        }
      }
      setSucesso(systemId);
    } finally { setSalvando(false); }
  };

  const handleDeletar = async (id: string, nome: string) => {
    if (!window.confirm(`Excluir o profissional "${nome}"?\n\nEsta ação não pode ser desfeita.`)) return;
    setSalvando(true);
    try {
      await apiClient.delete(`/professionals/${id}`);
      fetchProfissionais();
      if (modalDetalhe?.id === id) setModalDetalhe(null);
      if (modalEditar?.id === id) setModalEditar(null);
    } catch { alert('Erro ao excluir profissional'); }
    finally { setSalvando(false); }
  };

  const resetModal = () => {
    setEtapa(1); setErro(null); setSucesso(null); setProfId(null);
    setSystemId(''); setIdDisponivel(null); setRegistroGerado('');
    setFotoFile(null); setFotoPreview(null); setSlotDoc(slotVazio());
    setForm({ fullName:'', cpf:'', birthdate:'', phone:'', email:'',
              address:'', city:'', state:'', postalCode:'', affiliateId:'', registroFepcssp:'', identidadeInternacional:'' });
    setForm2({ funcao:'', dataInicioAtividade:'', experienciaAnos:'', especializacoes:'' });
    setFormErros({});
  };

  const fecharModal = () => { resetModal(); setModalCadastro(false); };
const abrirModal  = () => { resetModal(); setModalCadastro(true); 
  setEscolasRefresh(r => r + 1); };

  const ETAPAS = ['Dados Pessoais', 'Profissão', 'Foto e Doc'];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">Profissionais</h2>
            <p className="text-gray-500 text-sm">Cadastro e gestão de profissionais credenciados</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchProfissionais}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all text-sm">
              <RefreshCw className="w-4 h-4" /> Atualizar
            </button>
            <button onClick={abrirModal}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold hover:shadow-lg hover:shadow-purple-200 transition-all text-sm">
              <Plus className="w-4 h-4" /> Novo Profissional
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Cadastrados', value: totalRegistros },
            { label: 'Esta Página',       value: profissionais.length },
            { label: 'Páginas',           value: totalPaginas || 1 },
          ].map(s => (
            <div key={s.label} className="bg-cosmos-50/60 rounded-2xl p-4 border border-cosmos-100/70">
              <p className={text.label}>{s.label}</p>
              <p className="text-2xl font-semibold text-cosmos-900 mt-1">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
            placeholder="Buscar por nome, CPF, ID PRF-, número FEPCSSP..."
            className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 font-semibold text-sm bg-gray-50 focus:bg-white transition-all" />
        </div>
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
            <p className="text-gray-500 font-semibold text-sm">Carregando profissionais...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 font-bold">{error}</p>
            <button onClick={fetchProfissionais}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 text-sm">
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    {['Profissional', 'ID / FEPCSSP', 'Contato', 'Função', 'Escola', ''].map(h => (
                      <th key={h} className={`px-5 py-3.5 text-xs font-black text-gray-500 uppercase tracking-wider ${h === '' ? 'text-right' : 'text-left'}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {profissionais.map(p => (
                    <tr key={p.id} className="hover:bg-purple-50/30 transition-colors group">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <ProfAvatar prof={p} size="sm" />
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{p.fullName}</p>
                            <p className="text-xs text-gray-400">{fmtDate(p.birthdate)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-xs font-mono text-purple-600 font-bold">{p.systemId || '—'}</p>
                        {p.registroFepcssp && (
                          <p className="text-[11px] font-mono text-gray-400 mt-0.5">FEPCSSP: {p.registroFepcssp}</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-gray-700">{p.email || '—'}</p>
                        <p className="text-xs text-gray-400">{p.phone || '—'}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm font-semibold text-gray-900">{p.funcao || '—'}</p>
                        {p.experienciaAnos !== null && (
                          <p className="text-xs text-gray-400">{p.experienciaAnos} anos exp.</p>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-600 truncate max-w-[120px]">{p.affiliateName || '—'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {/* Ver detalhes */}
                          <button onClick={() => setModalDetalhe(p)}
                            className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </button>
                          {/* ✅ NOVO: Editar */}
                          <button onClick={() => abrirEditar(p)}
                            className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors" title="Editar profissional">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeletar(p.id, p.fullName)} disabled={salvando}
                            className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {profissionais.length === 0 && !loading && (
              <div className="py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-purple-300" />
                </div>
                <p className="text-gray-500 font-semibold">Nenhum profissional encontrado</p>
                <button onClick={abrirModal}
                  className="mt-4 px-5 py-2.5 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 text-sm">
                  Cadastrar primeiro profissional
                </button>
              </div>
            )}

            <div className="px-6 py-4 border-t border-cosmos-100 flex items-center justify-between">
              <p className="text-xs text-cosmos-500">
                Total: <span className="font-semibold text-cosmos-900">{totalRegistros}</span> profissionais
              </p>
              {totalPaginas > 1 && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setPaginaAtual(p => Math.max(1, p - 1))} disabled={paginaAtual === 1}
                    className="p-2 rounded-lg border border-cosmos-100 text-cosmos-500 hover:bg-cosmos-50 disabled:opacity-40 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-cosmos-600 font-semibold px-2">{paginaAtual} / {totalPaginas}</span>
                  <button onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))} disabled={paginaAtual === totalPaginas}
                    className="p-2 rounded-lg border border-cosmos-100 text-cosmos-500 hover:bg-cosmos-50 disabled:opacity-40 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          MODAL VER DETALHES — visual melhorado
      ══════════════════════════════════════════════════════════════════ */}
      {modalDetalhe && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl">

            {/* Header */}
            <div className="relative p-6 bg-cosmos-700 rounded-t-2xl overflow-hidden">
              <div className="relative flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <ProfAvatar prof={modalDetalhe} size="lg" />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-cosmos-700 flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-cosmos-900" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white leading-tight tracking-tight">{modalDetalhe.fullName}</h3>
                    {modalDetalhe.funcao && (
                      <p className="text-white/60 text-sm font-medium mt-0.5">{modalDetalhe.funcao}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 bg-white/15 text-white text-xs font-medium px-2.5 py-1 rounded-lg font-mono">
                        <Hash className="w-3 h-3 text-gold-400" />{modalDetalhe.systemId || '—'}
                      </span>
                      {modalDetalhe.registroFepcssp && (
                        <span className="inline-flex items-center gap-1 bg-white/15 text-white text-xs font-medium px-2.5 py-1 rounded-lg">
                          FEPCSSP: {modalDetalhe.registroFepcssp}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => setModalDetalhe(null)}
                  className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Dados pessoais */}
              <div>
                <p className={`${text.label} mb-3 flex items-center gap-2`}>
                  <User className="w-3.5 h-3.5" /> Dados Pessoais
                </p>
                <div className="grid grid-cols-2 gap-2.5">
                  <InfoRow label="CPF"         value={modalDetalhe.cpf}               icon={Hash}     mono />
                  <InfoRow label="Identidade internacional" value={modalDetalhe.identidadeInternacional} icon={Hash} mono />
                  <InfoRow label="Nascimento"  value={fmtDate(modalDetalhe.birthdate)} icon={Calendar} />
                  <InfoRow label="E-mail"      value={modalDetalhe.email}              icon={Mail}     />
                  <InfoRow label="Telefone"    value={modalDetalhe.phone}              icon={Phone}    />
                  <InfoRow label="Endereço"    value={modalDetalhe.address}            icon={MapPin}   />
                  <InfoRow label="Cidade / UF" value={[modalDetalhe.city, modalDetalhe.state].filter(Boolean).join(', ')} icon={MapPin} />
                </div>
              </div>

              {/* Atuação */}
              {(modalDetalhe.funcao || modalDetalhe.experienciaAnos != null || modalDetalhe.dataInicioAtividade) && (
                <div>
                  <p className={`${text.label} mb-3 flex items-center gap-2`}>
                    <Briefcase className="w-3.5 h-3.5" /> Atuação Profissional
                  </p>
                  <div className="grid grid-cols-2 gap-2.5">
                    <InfoRow label="Função"        value={modalDetalhe.funcao}              icon={Briefcase} />
                    <InfoRow label="Escola"        value={modalDetalhe.affiliateName}       icon={Building2} />
                    <InfoRow label="Experiência"   value={modalDetalhe.experienciaAnos != null ? `${modalDetalhe.experienciaAnos} anos` : null} icon={Clock} />
                    <InfoRow label="Início na área" value={fmtDate(modalDetalhe.dataInicioAtividade)} icon={Calendar} />
                  </div>
                </div>
              )}

              {modalDetalhe.especializacoes && (
                <div className="bg-cosmos-50/60 rounded-xl p-4 border border-cosmos-100">
                  <p className={`${text.label} mb-1.5`}>Especializações</p>
                  <p className="text-sm text-cosmos-800 leading-relaxed">{modalDetalhe.especializacoes}</p>
                </div>
              )}

              <p className="text-xs text-cosmos-400 flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" />
                Cadastrado em {fmtDate(modalDetalhe.createdAt)}
              </p>
            </div>

            <div className="px-6 pb-6 flex items-center gap-2">
              <button onClick={() => setModalDetalhe(null)}
                className="px-3 py-2 rounded-lg border border-cosmos-200 text-cosmos-600 text-sm font-medium hover:bg-cosmos-50 transition-colors">
                Fechar
              </button>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => abrirEditar(modalDetalhe)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-cosmos-900 text-white text-sm font-medium hover:bg-cosmos-800 transition-colors">
                  <Edit className="w-4 h-4" /> Editar
                </button>
                <button onClick={() => handleDeletar(modalDetalhe.id, modalDetalhe.fullName)} disabled={salvando}
                  className="p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors disabled:opacity-60">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL EDITAR — completo, espelhando o cadastro
      ══════════════════════════════════════════════════════════════════ */}
      {modalEditar && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Edit className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-gray-900">Editar Profissional</h2>
                  <p className="text-xs font-mono text-amber-600">{modalEditar.systemId}</p>
                </div>
              </div>
              <button onClick={() => setModalEditar(null)} className="p-2 hover:bg-amber-100 rounded-xl text-gray-500 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {erroEdit && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm font-semibold">{erroEdit}</p>
                </div>
              )}

              {/* ── Foto ── */}
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Foto de Perfil</p>
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 flex-shrink-0">
                    {fotoPreviewEdit ? (
                      <img src={fotoPreviewEdit} alt="Preview"
                        className="w-20 h-20 rounded-2xl object-cover border-2 border-amber-200" />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center border-2 border-dashed border-purple-200">
                        <User className="w-8 h-8 text-purple-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-200 cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-all text-sm font-semibold text-gray-600">
                      <Camera className="w-4 h-4 text-gray-400" />
                      {fotoFileEdit ? fotoFileEdit.name : 'Clique para trocar a foto'}
                      <input ref={fotoRefEdit} type="file" accept="image/*"
                        onChange={e => handleFoto(e, 'edit')} className="hidden" />
                    </label>
                    <p className="text-xs text-gray-400 mt-1.5 ml-1">JPG, PNG — máx. 5 MB</p>
                  </div>
                  {fotoFileEdit && (
                    <button onClick={() => { setFotoFileEdit(null); setFotoPreviewEdit(modalEditar.photoUrl ?? null); if (fotoRefEdit.current) fotoRefEdit.current.value = ''; }}
                      className="p-2 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* ── Identificação ── */}
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Identificação</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>Nome Completo</label>
                    <input value={formEdit.fullName}
                      onChange={e => setFormEdit(p => ({ ...p, fullName: e.target.value }))}
                      className={inputCls()} placeholder="Nome do profissional" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>CPF</label>
                      <input value={formEdit.cpf}
                        onChange={e => setFormEdit(p => ({ ...p, cpf: e.target.value.replace(/\D/g,'').slice(0,11) }))}
                        className={inputCls()} placeholder="Somente números" maxLength={11} />
                    </div>
                    <div>
                      <label className={labelCls}>Data de Nascimento</label>
                      <input type="date" value={formEdit.birthdate}
                        onChange={e => setFormEdit(p => ({ ...p, birthdate: e.target.value }))}
                        className={inputCls()} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Nº FEPCSSP</label>
                    <input value={formEdit.registroFepcssp}
                      onChange={e => setFormEdit(p => ({ ...p, registroFepcssp: e.target.value.replace(/\D/g,'').slice(0,6) }))}
                      className={inputCls()} placeholder="000000" maxLength={6} />
                    <p className="text-xs text-gray-400 mt-1">Número oficial da federação — 6 dígitos</p>
                  </div>
                  <div>
                    <label className={labelCls}>Identidade internacional</label>
                    <input value={formEdit.identidadeInternacional}
                      onChange={e => setFormEdit(p => ({ ...p, identidadeInternacional: e.target.value.replace(/[^a-zA-Z0-9]/g,'').toUpperCase().slice(0,15) }))}
                      className={inputCls()} placeholder="AB12CD34EF56GH7" maxLength={15} />
                    <p className="text-xs text-gray-400 mt-1">Exatamente 15 letras ou números</p>
                  </div>
                </div>
              </div>

              {/* ── Contato ── */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Contato</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Telefone</label>
                    <input value={formEdit.phone}
                      onChange={e => setFormEdit(p => ({ ...p, phone: e.target.value }))}
                      className={inputCls()} placeholder="(00) 00000-0000" />
                  </div>
                  <div>
                    <label className={labelCls}>E-mail</label>
                    <input type="email" value={formEdit.email}
                      onChange={e => setFormEdit(p => ({ ...p, email: e.target.value }))}
                      className={inputCls()} placeholder="email@dominio.com" />
                  </div>
                </div>
              </div>

              {/* ── Endereço ── */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Endereço</p>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>CEP</label>
                    <div className="flex gap-2">
                      <input value={formEdit.postalCode}
                        onChange={e => setFormEdit(p => ({ ...p, postalCode: e.target.value.replace(/\D/g,'').slice(0,8) }))}
                        className={`${inputCls()} flex-1`} placeholder="00000000" maxLength={8} />
                      <button type="button" onClick={() => buscarCEP(formEdit.postalCode, 'edit')}
                        className="px-4 py-3 bg-gray-100 border border-gray-200 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 flex items-center gap-2 flex-shrink-0">
                        {cepLoadingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Buscar
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Endereço</label>
                    <input value={formEdit.address}
                      onChange={e => setFormEdit(p => ({ ...p, address: e.target.value }))}
                      className={inputCls()} placeholder="Rua, número, complemento" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Cidade</label>
                      <input value={formEdit.city}
                        onChange={e => setFormEdit(p => ({ ...p, city: e.target.value }))}
                        className={inputCls()} />
                    </div>
                    <div>
                      <label className={labelCls}>UF</label>
                      <select value={formEdit.state}
                        onChange={e => setFormEdit(p => ({ ...p, state: e.target.value }))}
                        className={inputCls()}>
                        <option value="">—</option>
                        {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                      </select>
                    </div>
                   <div>
  <label className={labelCls}>Escola Vinculada</label>
  <EscolaSearch
    escolas={escolas}
    loading={escolasLoading}
    value={formEdit.affiliateId}
    onChange={id => setFormEdit(p => ({ ...p, affiliateId: id }))}
  />
</div>
                  </div>
                </div>
              </div>

              {/* ── Profissão ── */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Atuação Profissional</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Função / Profissão</label>
                      <input value={formEdit.funcao}
                        onChange={e => setFormEdit(p => ({ ...p, funcao: e.target.value }))}
                        className={inputCls()} placeholder="Cabeleireiro, Esteticista..." />
                    </div>
                    <div>
                      <label className={labelCls}>Data de Início na Área</label>
                      <input type="date" value={formEdit.dataInicioAtividade}
                        onChange={e => setFormEdit(p => ({ ...p, dataInicioAtividade: e.target.value }))}
                        className={inputCls()} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Anos de Experiência</label>
                    <input type="number" min={0} value={formEdit.experienciaAnos}
                      onChange={e => setFormEdit(p => ({ ...p, experienciaAnos: e.target.value }))}
                      className={inputCls()} placeholder="Ex: 5" />
                  </div>
                  <div>
                    <label className={labelCls}>Especializações</label>
                    <textarea value={formEdit.especializacoes}
                      onChange={e => setFormEdit(p => ({ ...p, especializacoes: e.target.value }))}
                      rows={3} className={`${inputCls()} resize-none`}
                      placeholder="Corte, coloração, tratamentos capilares..." />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex gap-3 flex-shrink-0">
              <button onClick={() => setModalEditar(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-50 text-sm transition-colors">
                Cancelar
              </button>
              <button onClick={handleSalvarEdicao} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 flex items-center justify-center gap-2 text-sm disabled:opacity-60 transition-colors">
                {salvando ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</> : <><Save className="w-4 h-4" /> Salvar Alterações</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODAL CADASTRO
      ══════════════════════════════════════════════════════════════════ */}
      {modalCadastro && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[94vh] overflow-hidden flex flex-col shadow-2xl">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50 flex-shrink-0">
              <div>
                <h2 className="text-lg font-black text-gray-900">Novo Profissional</h2>
                <p className="text-sm text-gray-500">Etapa {etapa} de 3 — {ETAPAS[etapa - 1]}</p>
              </div>
              <div className="hidden md:flex items-center gap-2 mr-4">
                {ETAPAS.map((label, i) => {
                  const n = i + 1;
                  const done = etapa > n; const active = etapa === n;
                  return (
                    <React.Fragment key={n}>
                      {i > 0 && <div className="w-6 h-px bg-gray-200" />}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black border-2 transition-all
                        ${done ? 'bg-green-500 border-green-500 text-white'
                        : active ? 'bg-purple-600 border-purple-600 text-white'
                        : 'bg-white border-gray-200 text-gray-400'}`}>
                        {done ? '✓' : n}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              <button onClick={fecharModal} className="p-2 hover:bg-purple-100 rounded-xl text-gray-500 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {erro && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm font-semibold">{erro}</p>
                </div>
              )}

              {/* ETAPA 1 */}
              {etapa === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Nome Completo *</label>
                    <input value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                      className={inputCls(!!formErros.fullName)} placeholder="Nome do profissional" />
                    {formErros.fullName && <p className="text-xs text-red-500 mt-1">{formErros.fullName}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>CPF *</label>
                      <input value={form.cpf}
                        onChange={e => setForm(p => ({ ...p, cpf: e.target.value.replace(/\D/g,'').slice(0,11) }))}
                        className={inputCls(!!formErros.cpf)} placeholder="Somente números" maxLength={11} />
                      {formErros.cpf && <p className="text-xs text-red-500 mt-1">{formErros.cpf}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Data de Nascimento *</label>
                      <input type="date" value={form.birthdate}
                        onChange={e => setForm(p => ({ ...p, birthdate: e.target.value }))}
                        className={inputCls(!!formErros.birthdate)} />
                      {formErros.birthdate && <p className="text-xs text-red-500 mt-1">{formErros.birthdate}</p>}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>ID do Profissional *</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input value={systemId}
                          onChange={e => { setSystemId(e.target.value.toUpperCase()); setIdDisponivel(null); }}
                          className={`${inputCls(!!formErros.systemId)} font-mono pr-8 ${idDisponivel === true ? 'border-green-400' : idDisponivel === false ? 'border-red-400' : ''}`}
                          placeholder="PRF-2026-XXXXXX" />
                        {idDisponivel === true  && <CheckCircle className="absolute right-3 top-3.5 w-4 h-4 text-green-500" />}
                        {idDisponivel === false && <AlertCircle className="absolute right-3 top-3.5 w-4 h-4 text-red-500" />}
                      </div>
                      <button type="button" onClick={gerarSystemId} disabled={gerandoId}
                        className="px-4 py-3 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
                        {gerandoId ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Gerar ID
                      </button>
                    </div>
                    {formErros.systemId && <p className="text-xs text-red-500 mt-1">{formErros.systemId}</p>}
                    {idDisponivel === true && <p className="text-xs text-green-600 mt-1">✓ ID disponível</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Nº FEPCSSP <span className="text-gray-400 normal-case font-normal">(vazio = automático)</span></label>
                    <input value={form.registroFepcssp}
                      onChange={e => setForm(p => ({ ...p, registroFepcssp: e.target.value.replace(/\D/g,'').slice(0,6) }))}
                      className={inputCls()} placeholder="000000" maxLength={6} />
                  </div>
                  <div>
                    <label className={labelCls}>Identidade internacional <span className="text-gray-400 normal-case font-normal">(opcional)</span></label>
                    <input value={form.identidadeInternacional}
                      onChange={e => setForm(p => ({ ...p, identidadeInternacional: e.target.value.replace(/[^a-zA-Z0-9]/g,'').toUpperCase().slice(0,15) }))}
                      className={inputCls(!!formErros.identidadeInternacional)} placeholder="AB12CD34EF56GH7" maxLength={15} />
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-gray-400">Exatamente 15 letras ou números</p>
                      <p className="text-xs text-gray-400 font-mono">{form.identidadeInternacional.length}/15</p>
                    </div>
                    {formErros.identidadeInternacional && <p className="text-xs text-red-500 mt-1">{formErros.identidadeInternacional}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Telefone *</label>
                      <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                        className={inputCls(!!formErros.phone)} placeholder="(00) 00000-0000" />
                      {formErros.phone && <p className="text-xs text-red-500 mt-1">{formErros.phone}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>E-mail</label>
                      <input type="email" value={form.email}
                        onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                        className={inputCls()} placeholder="email@dominio.com" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>CEP</label>
                    <div className="flex gap-2">
                      <input value={form.postalCode}
                        onChange={e => setForm(p => ({ ...p, postalCode: e.target.value.replace(/\D/g,'').slice(0,8) }))}
                        className={`${inputCls()} flex-1`} placeholder="00000000" maxLength={8} />
                      <button type="button" onClick={() => buscarCEP(form.postalCode, 'cadastro')}
                        className="px-4 py-3 bg-gray-100 border border-gray-200 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 flex items-center gap-2">
                        {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Buscar
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Endereço</label>
                    <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                      className={inputCls()} placeholder="Rua, número, complemento" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Cidade</label>
                      <input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className={inputCls()} />
                    </div>
                    <div>
                      <label className={labelCls}>UF</label>
                      <select value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} className={inputCls()}>
                        <option value="">—</option>
                        {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                      </select>
                    </div>
                   <div>
  <label className={labelCls}>Escola</label>
  <EscolaSearch
    escolas={escolas}
    loading={escolasLoading}
    value={form.affiliateId}
    onChange={id => setForm(p => ({ ...p, affiliateId: id }))}
  />
</div>
                  </div>
                </div>
              )}

              {/* ETAPA 2 */}
              {etapa === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Função / Profissão</label>
                      <input value={form2.funcao}
                        onChange={e => setForm2(p => ({ ...p, funcao: e.target.value }))}
                        className={inputCls()} placeholder="Cabeleireiro, Esteticista..." />
                    </div>
                    <div>
                      <label className={labelCls}>Data de Início na Área</label>
                      <input type="date" value={form2.dataInicioAtividade}
                        onChange={e => setForm2(p => ({ ...p, dataInicioAtividade: e.target.value }))}
                        className={inputCls()} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Anos de Experiência <span className="text-gray-400 normal-case font-normal">(opcional)</span></label>
                    <input type="number" min={0} value={form2.experienciaAnos}
                      onChange={e => setForm2(p => ({ ...p, experienciaAnos: e.target.value }))}
                      className={inputCls()} placeholder="Ex: 5" />
                  </div>
                  <div>
                    <label className={labelCls}>Especializações <span className="text-gray-400 normal-case font-normal">(opcional)</span></label>
                    <textarea value={form2.especializacoes}
                      onChange={e => setForm2(p => ({ ...p, especializacoes: e.target.value }))}
                      rows={4} className={`${inputCls()} resize-none`}
                      placeholder="Corte, coloração, tratamentos capilares..." />
                  </div>
                </div>
              )}

              {/* ETAPA 3 */}
              {etapa === 3 && (
                <div className="space-y-5">
                  {sucesso ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-4 text-center">
                      <div className="w-20 h-20 rounded-2xl bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-green-500" />
                      </div>
                      <div>
                        <p className="text-xl font-black text-green-700">Profissional cadastrado!</p>
                        <p className="text-sm font-mono text-gray-600 mt-1 bg-gray-100 px-3 py-1.5 rounded-lg inline-block">{sucesso}</p>
                        {registroGerado && (
                          <p className="text-sm font-mono text-purple-600 mt-2">FEPCSSP: <strong>{registroGerado}</strong></p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-purple-600 font-semibold">Profissional cadastrado</p>
                          <p className="text-lg font-black text-purple-800 font-mono">{systemId}</p>
                          {registroGerado && (
                            <p className="text-xs text-purple-500 mt-0.5">Nº FEPCSSP: {registroGerado}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>Foto de Perfil <span className="text-gray-400 normal-case font-normal">(opcional)</span></label>
                        {fotoPreview ? (
                          <div className="flex items-center gap-4 p-4 border-2 border-purple-300 bg-purple-50/30 rounded-xl">
                            <img src={fotoPreview} alt="Preview" className="w-20 h-20 rounded-xl object-cover border-2 border-purple-300 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-700 truncate">{fotoFile?.name}</p>
                              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <CheckCircle className="w-3.5 h-3.5" /> Foto selecionada
                              </p>
                            </div>
                            <button type="button" onClick={() => { setFotoFile(null); setFotoPreview(null); if (fotoRef.current) fotoRef.current.value = ''; }}
                              className="p-2 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 flex-shrink-0">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center gap-3 py-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50/20 transition-all">
                            <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
                              <Camera className="w-6 h-6 text-gray-400" />
                            </div>
                            <div className="text-center">
                              <p className="text-sm font-semibold text-gray-600">Clique para selecionar foto</p>
                              <p className="text-xs text-gray-400 mt-1">JPG, PNG — máx. 5 MB</p>
                            </div>
                            <input ref={fotoRef} type="file" accept="image/*" onChange={e => handleFoto(e, 'cadastro')} className="hidden" />
                          </label>
                        )}
                      </div>
                      <DocCard
                        titulo="Documento (RG, CPF ou Diploma)"
                        descricao="Apenas 1 documento — arquivado na plataforma"
                        slot={slotDoc} accept="image/*,.pdf"
                        onSelecionar={selecionarDoc} onRemover={removerDoc}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between flex-shrink-0">
              <button
                onClick={() => etapa > 1 && !sucesso ? setEtapa((etapa - 1) as 1|2|3) : fecharModal()}
                disabled={(etapa === 3 && !sucesso) || salvando}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 font-bold hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm">
                <ChevronLeft className="w-4 h-4" />
                {etapa === 1 ? 'Cancelar' : 'Anterior'}
              </button>

              {etapa === 1 && (
                <button onClick={() => { if (validar1()) setEtapa(2); }}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors text-sm">
                  Próxima <ChevronRight className="w-4 h-4" />
                </button>
              )}
              {etapa === 2 && (
                <button onClick={handleCadastrar} disabled={salvando}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 disabled:opacity-60 transition-colors text-sm">
                  {salvando ? <><Loader2 className="w-4 h-4 animate-spin" /> Cadastrando...</> : <>Cadastrar <ChevronRight className="w-4 h-4" /></>}
                </button>
              )}
              {etapa === 3 && !sucesso && (
                <div className="flex items-center gap-3">
                  <button onClick={() => { fetchProfissionais(); fecharModal(); }} disabled={salvando}
                    className="px-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-100 disabled:opacity-40 transition-colors">
                    Pular
                  </button>
                  <button onClick={handleFinalizar} disabled={salvando || (!fotoFile && !slotDoc.arquivo)}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-50 transition-colors text-sm">
                    {salvando ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4" /> Enviar e Finalizar</>}
                  </button>
                </div>
              )}
              {etapa === 3 && sucesso && (
                <button onClick={() => { fetchProfissionais(); fecharModal(); }}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition-colors text-sm">
                  <CheckCircle className="w-4 h-4" /> Concluir
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciamentoProfissionaisADM;
